// © 2025 Camp Promethean, LLC. All Rights Reserved.
//
// ReviewCard — Hands-Free review + commit UI (workflow-aware as of T-205).
//
// Renders an hf_review_items row for ANY supported workflow (runoff_log,
// yield_batch, diary_entry) from a per-workflow field descriptor
// (workflowFields.ts), lets the reviewer correct allowed fields, and calls
// hands-free-commit to write the final row to the workflow's production table.
//
// Editable fields and which are required-before-commit come from the descriptor;
// the descriptor keys are a subset of the matching workflow's
// ALLOWED_OVERRIDE_KEYS in supabase/functions/hands-free-commit.
//
// NOT editable here: facility_id, user_id / created_by, workflow_type, status.

import { useCallback, useEffect, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Loader2,
  RefreshCw,
  Wand2,
  XCircle,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { hfCard, hfCardHeader, hfCardTitle, hfSuccessAlert, hfWarnAlert, hfWarnBody, hfWarnTitle } from "./handsFreeUi";
import { useHandsFreeEntityOptions } from "./useHandsFreeEntityOptions";
import type {
  CommitErrorResponse,
  CommitSuccessResponse,
  HfReviewItem,
  MeasurementValue,
} from "./types";
import { WORKFLOW_LABELS } from "./types";
import {
  buildFieldOverrides,
  type FieldDesc,
  type FormValues,
  getDescriptor,
  isDirty,
  proposedDataToFormValues,
  requiredFieldsFilled,
} from "./workflowFields";
import { useReviewItem } from "./useReviewItem";

interface ReviewCardProps {
  reviewItemId: string;
  initialData?: HfReviewItem;
  onCommitted?: (committedRecordId: string) => void;
  onRejected?: (reviewItemId: string) => void;
}

// ── Status badge helpers ──────────────────────────────────────────────────────

function statusBadgeVariant(
  status: string,
): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "pending_review":
      return "secondary";
    case "committed":
      return "default";
    case "rejected":
    case "error":
      return "destructive";
    default:
      return "outline";
  }
}

function statusLabel(status: string): string {
  switch (status) {
    case "pending_review":
      return "Ready to Review";
    case "review_required":
      return "Review Required";
    case "committed":
      return "Committed";
    case "rejected":
      return "Rejected";
    case "error":
      return "Error";
    case "approved":
      return "Approved";
    default:
      return status;
  }
}

// ── Small presentational helpers ──────────────────────────────────────────────

function FieldLabel({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("mb-1.5 text-sm font-medium text-foreground", className)}>
      {children}
    </div>
  );
}

function AIBadge({ modified }: { modified: boolean }) {
  if (modified) {
    return (
      <span className="ml-1.5 inline-flex items-center rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] font-medium text-amber-200">
        edited
      </span>
    );
  }
  return (
    <span className="ml-1.5 inline-flex items-center gap-0.5 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
      <Wand2 className="h-2.5 w-2.5" />
      AI
    </span>
  );
}

function MeasurementValuesDisplay({ values }: { values: MeasurementValue[] }) {
  const [expanded, setExpanded] = useState(false);
  if (!values || values.length === 0) return null;
  const grouped = values.reduce<Record<string, MeasurementValue[]>>((acc, v) => {
    (acc[v.type] ??= []).push(v);
    return acc;
  }, {});
  return (
    <div className="rounded-lg bg-muted/40 px-3 py-2">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        {values.length} individual reading{values.length !== 1 ? "s" : ""}
      </button>
      {expanded && (
        <div className="mt-2 space-y-2 border-t border-border/60 pt-2">
          {Object.entries(grouped).map(([type, items]) => (
            <div key={type}>
              <span className="text-xs font-medium capitalize text-muted-foreground">{type}</span>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {items.map((mv, i) => (
                  <span key={i} className="rounded-md bg-background px-2 py-0.5 text-xs tabular-nums">
                    {mv.value}{mv.unit ? ` ${mv.unit}` : ""}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Single field input ────────────────────────────────────────────────────────

function FieldInput({
  field,
  value,
  modified,
  disabled,
  onChange,
  entityOptions,
}: {
  field: FieldDesc;
  value: string;
  modified: boolean;
  disabled: boolean;
  onChange: (v: string) => void;
  entityOptions?: { rooms: Array<{ value: string; label: string }>; zones: Array<{ value: string; label: string }>; crops: Array<{ value: string; label: string }> };
}) {
  const missingRequired = field.required && value.trim() === "";
  const labelEl = (
    <FieldLabel>
      {field.label}
      {!disabled && <AIBadge modified={modified} />}
      {missingRequired && (
        <span className="ml-1.5 text-[10px] font-medium text-destructive">required</span>
      )}
    </FieldLabel>
  );

  const selectOptions =
    field.entity === "room"
      ? entityOptions?.rooms
      : field.entity === "zone"
        ? entityOptions?.zones
        : field.entity === "crop"
          ? entityOptions?.crops
          : field.options;

  let control: React.ReactNode;
  if (field.type === "textarea") {
    control = (
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
        className="resize-none text-sm"
        rows={2}
        disabled={disabled}
      />
    );
  } else if (field.type === "select" || field.entity) {
    control = (
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={cn(
          "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm",
          "ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          "disabled:cursor-not-allowed disabled:opacity-50",
          missingRequired && "border-destructive focus-visible:ring-destructive",
        )}
      >
        {(selectOptions ?? field.options ?? []).map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    );
  } else {
    const inputType = field.type === "number"
      ? "number"
      : field.type === "date" || field.type === "datetime"
      ? "date"
      : "text";
    control = (
      <Input
        type={inputType}
        step={field.type === "number" ? "0.01" : undefined}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
        className={cn(
          "text-sm",
          field.mono && "font-mono",
          missingRequired && "border-destructive focus-visible:ring-destructive",
        )}
        disabled={disabled}
      />
    );
  }

  return (
    <div className="flex-1">
      {labelEl}
      {control}
      {field.help && missingRequired && (
        <p className="mt-1 text-xs text-muted-foreground">{field.help}</p>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function ReviewCard({
  reviewItemId,
  initialData,
  onCommitted,
  onRejected,
}: ReviewCardProps) {
  const { reviewItem, loading, error: loadError, refresh } = useReviewItem(
    reviewItemId,
    initialData,
  );
  const entityOptions = useHandsFreeEntityOptions();

  const [formValues, setFormValues] = useState<FormValues | null>(null);
  const [formInitialisedFor, setFormInitialisedFor] = useState<string | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [commitResult, setCommitResult] = useState<
    | { ok: true; data: CommitSuccessResponse }
    | { ok: false; data: CommitErrorResponse }
    | null
  >(null);

  const [rejecting, setRejecting] = useState(false);
  const [rejectResult, setRejectResult] = useState<
    | { ok: true }
    | { ok: false; error: string }
    | null
  >(null);

  const descriptor = reviewItem ? getDescriptor(reviewItem.workflow_type) : null;

  useEffect(() => {
    if (!reviewItem || !descriptor) return;
    if (
      formInitialisedFor === reviewItem.id &&
      ["committed", "rejected", "error"].includes(reviewItem.status)
    ) {
      return;
    }
    if (formInitialisedFor !== reviewItem.id) {
      setFormValues(proposedDataToFormValues(descriptor, reviewItem.proposed_data));
      setFormInitialisedFor(reviewItem.id);
      setCommitResult(null);
    }
  }, [reviewItem, descriptor, formInitialisedFor]);

  const handleFieldChange = useCallback((field: string, value: string) => {
    setFormValues((prev) => (prev ? { ...prev, [field]: value } : prev));
  }, []);

  const handleReset = useCallback(() => {
    if (!reviewItem || !descriptor) return;
    setFormValues(proposedDataToFormValues(descriptor, reviewItem.proposed_data));
    setCommitResult(null);
  }, [reviewItem, descriptor]);

  const getSupabaseUrl = () =>
    ((import.meta as Record<string, unknown>).env?.VITE_SUPABASE_URL as
      | string
      | undefined) || "https://bjvlynqjoetvrbbfvhod.supabase.co";

  const handleCommit = useCallback(async () => {
    if (!reviewItem || !descriptor || !formValues || submitting) return;

    const overrides = buildFieldOverrides(descriptor, reviewItem.proposed_data, formValues);

    setSubmitting(true);
    setCommitResult(null);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) {
        setCommitResult({ ok: false, data: { error: "You must be logged in to commit records." } });
        return;
      }
      const response = await fetch(`${getSupabaseUrl()}/functions/v1/hands-free-commit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          review_item_id: reviewItemId,
          field_overrides: isDirty(overrides) ? overrides : undefined,
        }),
      });
      const body = (await response.json()) as CommitSuccessResponse | CommitErrorResponse;
      if (response.ok) {
        const success = body as CommitSuccessResponse;
        setCommitResult({ ok: true, data: success });
        onCommitted?.(success.committed_record_id);
        refresh().catch(console.error);
      } else {
        setCommitResult({ ok: false, data: body as CommitErrorResponse });
      }
    } catch (err: unknown) {
      setCommitResult({
        ok: false,
        data: {
          error: err instanceof Error ? err.message : "Network error. Please try again.",
        },
      });
    } finally {
      setSubmitting(false);
    }
  }, [reviewItem, descriptor, formValues, submitting, reviewItemId, onCommitted, refresh]);

  const handleReject = useCallback(async () => {
    if (!reviewItem || rejecting || submitting) return;
    setRejecting(true);
    setRejectResult(null);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) {
        setRejectResult({ ok: false, error: "You must be logged in to reject items." });
        return;
      }
      const response = await fetch(`${getSupabaseUrl()}/functions/v1/hands-free-reject`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ review_item_id: reviewItemId }),
      });
      const body = (await response.json()) as Record<string, unknown>;
      if (response.ok) {
        setRejectResult({ ok: true });
        onRejected?.(reviewItemId);
        refresh().catch(console.error);
      } else {
        setRejectResult({
          ok: false,
          error: (body["error"] as string) ?? `Reject failed (HTTP ${response.status})`,
        });
      }
    } catch (err) {
      setRejectResult({
        ok: false,
        error: err instanceof Error ? err.message : "Network error during rejection.",
      });
    } finally {
      setRejecting(false);
    }
  }, [reviewItem, rejecting, submitting, reviewItemId, onRejected, refresh]);

  // ── Render: loading / error ──────────────────────────────────────────────────

  if (loading) {
    return (
      <Card className={hfCard}>
        <CardContent className="flex items-center justify-center gap-2 py-14">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Preparing your entry…</span>
        </CardContent>
      </Card>
    );
  }

  if (loadError || !reviewItem || !descriptor) {
    return (
      <Card className={hfCard}>
        <CardContent className="pt-6">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Couldn't load this entry</AlertTitle>
            <AlertDescription>
              {loadError ?? "This entry may have been removed or is no longer available."}
              <Button
                variant="ghost"
                size="sm"
                className="mt-2 flex items-center gap-1"
                onClick={() => refresh()}
              >
                <RefreshCw className="h-3.5 w-3.5" /> Try again
              </Button>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const proposed = reviewItem.proposed_data;
  const warnings = reviewItem.resolution_summary?.warnings ?? [];
  const isTerminal = ["committed", "rejected", "error"].includes(reviewItem.status);
  const isCommitEligible =
    reviewItem.status === "pending_review" || reviewItem.status === "review_required";
  const overrides = formValues ? buildFieldOverrides(descriptor, proposed, formValues) : {};
  const hasEdits = isDirty(overrides);
  const canCommit = formValues ? requiredFieldsFilled(descriptor, formValues) : false;
  const workflowLabel =
    WORKFLOW_LABELS[reviewItem.workflow_type] ?? reviewItem.workflow_type.replace(/_/g, " ");

  // Group consecutive fields that share a `group` id into one row.
  const rows: FieldDesc[][] = [];
  for (const f of descriptor.fields) {
    const last = rows[rows.length - 1];
    if (f.group && last && last[0].group === f.group) last.push(f);
    else rows.push([f]);
  }

  const measurements = (proposed.measurement_values as MeasurementValue[] | undefined) ?? [];

  function displayValue(field: FieldDesc, raw: string): string {
    if (!raw) return "—";
    if (field.entity === "zone") {
      return entityOptions.zones.find((z) => z.value === raw)?.label ?? raw;
    }
    if (field.entity === "room") {
      return entityOptions.rooms.find((r) => r.value === raw)?.label ?? raw;
    }
    if (field.entity === "crop") {
      return entityOptions.crops.find((c) => c.value === raw)?.label ?? raw;
    }
    if (field.type === "select" && field.options) {
      return field.options.find((o) => o.value === raw)?.label ?? raw;
    }
    return raw;
  }

  return (
    <div className="space-y-4">
      <Card className={hfCard}>
        <CardHeader className={cn(hfCardHeader, "pb-4")}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className={cn(hfCardTitle, "flex items-center gap-2")}>
                <Wand2 className="h-4 w-4 text-primary" />
                Review entry
              </CardTitle>
              <CardDescription className="mt-1">{workflowLabel}</CardDescription>
            </div>
            <Badge variant={statusBadgeVariant(reviewItem.status)} className="shrink-0">
              {statusLabel(reviewItem.status)}
            </Badge>
          </div>
        </CardHeader>
      </Card>

      {/* Resolution warnings */}
      {warnings.length > 0 && !isTerminal && (
        <Alert
          variant={reviewItem.status === "review_required" ? "destructive" : "default"}
          className={
            reviewItem.status === "review_required"
              ? undefined
              : hfWarnAlert
          }
        >
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>
            {reviewItem.status === "review_required"
              ? "Needs your attention"
              : "Heads up"}
          </AlertTitle>
          <AlertDescription>
            <ul className="mt-1 space-y-0.5">
              {warnings.map((w, i) => (
                <li key={i} className="text-sm">{w}</li>
              ))}
            </ul>
            {reviewItem.status === "review_required" && (
              <p className="mt-2 text-sm font-medium">Fix the highlighted fields below, then save.</p>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Proposed values + editable overrides */}
      {formValues && (
        <Card className={hfCard}>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium">What we captured</CardTitle>
            <CardDescription className="text-sm">
              Tap any field to correct it before saving.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {rows.map((row, ri) => (
              <div key={ri} className={row.length > 1 ? "grid gap-3" : undefined}
                style={row.length > 1 ? { gridTemplateColumns: `repeat(${row.length}, minmax(0, 1fr))` } : undefined}
              >
                {row.map((f) => {
                  const value = formValues[f.key] ?? "";
                  const fieldOverridden = Object.prototype.hasOwnProperty.call(overrides, f.key);
                  return isTerminal ? (
                    <div key={f.key} className="flex-1">
                      <FieldLabel>{f.label}</FieldLabel>
                      <p className="text-sm">{displayValue(f, value)}</p>
                    </div>
                  ) : (
                    <FieldInput
                      key={f.key}
                      field={f}
                      value={value}
                      modified={fieldOverridden}
                      disabled={submitting}
                      onChange={(v) => handleFieldChange(f.key, v)}
                      entityOptions={entityOptions}
                    />
                  );
                })}
              </div>
            ))}

            {descriptor.showMeasurements && measurements.length > 0 && (
              <>
                <Separator />
                <div>
                  <FieldLabel>Individual readings</FieldLabel>
                  <MeasurementValuesDisplay values={measurements} />
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Commit result */}
      {commitResult && (
        <Alert
          variant={commitResult.ok ? "default" : "destructive"}
          className={commitResult.ok ? hfSuccessAlert : undefined}
        >
          {commitResult.ok ? (
            <CheckCircle2 className="h-4 w-4 text-emerald-400" />
          ) : (
            <AlertTriangle className="h-4 w-4" />
          )}
          <AlertTitle>{commitResult.ok ? "Saved" : "Couldn't save"}</AlertTitle>
          <AlertDescription>
            {commitResult.ok ? (
              <span>Your entry has been saved to your records.</span>
            ) : (
              <>
                <p>{commitResult.data.error}</p>
                {commitResult.data.detail && (
                  <p className="mt-1 text-xs opacity-75">{commitResult.data.detail}</p>
                )}
              </>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Terminal state */}
      {isTerminal && !commitResult && (
        <Card className={cn(hfCard, "border-muted")}>
          <CardContent className="flex flex-col items-center gap-2 py-8 text-center text-sm text-muted-foreground">
            {reviewItem.status === "committed" ? (
              <>
                <CheckCircle2 className="h-8 w-8 text-primary" />
                <span className="font-medium text-foreground">Already saved</span>
              </>
            ) : (
              <span>
                This entry is <strong>{statusLabel(reviewItem.status).toLowerCase()}</strong> and can't be changed.
              </span>
            )}
          </CardContent>
        </Card>
      )}

      {/* Reject result */}
      {rejectResult && (
        <Alert
          variant={rejectResult.ok ? "default" : "destructive"}
          className={rejectResult.ok ? "border-muted bg-muted/50" : undefined}
        >
          {rejectResult.ok ? (
            <XCircle className="h-4 w-4 text-muted-foreground" />
          ) : (
            <AlertTriangle className="h-4 w-4" />
          )}
          <AlertTitle>{rejectResult.ok ? "Entry discarded" : "Couldn't discard"}</AlertTitle>
          {!rejectResult.ok && <AlertDescription>{rejectResult.error}</AlertDescription>}
        </Alert>
      )}

      {/* Actions */}
      {isCommitEligible && !commitResult?.ok && !rejectResult?.ok && (
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleReject}
            disabled={submitting || rejecting}
            className="text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
          >
            {rejecting ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            ) : (
              <XCircle className="mr-1.5 h-3.5 w-3.5" />
            )}
            Discard
          </Button>

          <div className="flex-1" />

          <Button
            variant="outline"
            size="sm"
            onClick={handleReset}
            disabled={submitting || rejecting || !hasEdits}
          >
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
            Undo edits
          </Button>

          <Button onClick={handleCommit} disabled={submitting || rejecting || !canCommit} className="min-w-[120px]">
            {submitting ? (
              <>
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                Saving…
              </>
            ) : (
              <>
                <CheckCircle2 className="mr-1.5 h-4 w-4" />
                Save
              </>
            )}
          </Button>
        </div>
      )}

      {commitResult?.ok && (
        <p className="text-center text-sm text-muted-foreground">
          All set — you can log another entry or close this panel.
        </p>
      )}
    </div>
  );
}

export default ReviewCard;
